import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { API, client, STATUS_META } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { toast, Toaster } from "sonner";
import { ChevronRight, X, Camera, Trash2, Check, Minus, Plus, AlertTriangle, CloudOff } from "lucide-react";

const JOB_TYPE_LABELS = {
  emergency: "Emergency", repair: "Repair", scheduled_pm: "Scheduled PM",
  warranty: "Warranty", contract: "Contract", quoted_work: "Quoted work",
};

const QUEUE_KEY = "blueflames-pending-jobs";
const memoryStore = {};
let storageWorks = true;

(function probeStorage() {
  try {
    window.localStorage.setItem("blueflames-probe", "1");
    window.localStorage.removeItem("blueflames-probe");
  } catch {
    storageWorks = false;
  }
})();

// Decode a base64 data URL into a Blob without a network request; null when malformed.
function dataUrlToBlob(dataUrl) {
  try {
    const [head, body] = dataUrl.split(",");
    const mime = /^data:([^;,]+);base64$/.exec(head)?.[1];
    if (!mime || !body) return null;
    const binary = atob(body);
    const bytes = new Uint8Array(binary.length);
    for (let i = 0; i < binary.length; i += 1) bytes[i] = binary.charCodeAt(i);
    return new Blob([bytes], { type: mime });
  } catch {
    return null;
  }
}

function readStore(key) {
  if (storageWorks) {
    try { return JSON.parse(window.localStorage.getItem(key) || "null"); }
    catch { storageWorks = false; }
  }
  return memoryStore[key] ?? null;
}

function writeStore(key, value) {
  memoryStore[key] = value;
  if (!storageWorks) return;
  try { window.localStorage.setItem(key, JSON.stringify(value)); }
  catch { storageWorks = false; }
}

function clearStore(key) {
  delete memoryStore[key];
  if (!storageWorks) return;
  try { window.localStorage.removeItem(key); } catch { storageWorks = false; }
}

function SignaturePad({ onChange, value, testid }) {
  const canvasRef = useRef(null);
  const drawing = useRef(false);
  const last = useRef(null);

  useEffect(() => {
    const c = canvasRef.current;
    const dpr = window.devicePixelRatio || 1;
    const rect = c.getBoundingClientRect();
    c.width = rect.width * dpr; c.height = rect.height * dpr;
    const ctx = c.getContext("2d");
    ctx.scale(dpr, dpr);
    ctx.strokeStyle = "#101820"; ctx.lineWidth = 2; ctx.lineCap = "round";
    if (typeof value === "string" && value) {
      const img = new Image(); img.onload = () => ctx.drawImage(img, 0, 0, rect.width, rect.height); img.src = value;
    }
  }, []);

  const pos = (e) => {
    const r = canvasRef.current.getBoundingClientRect();
    const t = e.touches?.[0];
    return { x: (t ? t.clientX : e.clientX) - r.left, y: (t ? t.clientY : e.clientY) - r.top };
  };
  const start = (e) => { e.preventDefault(); drawing.current = true; last.current = pos(e); };
  const move = (e) => {
    if (!drawing.current) return; e.preventDefault();
    const p = pos(e);
    const ctx = canvasRef.current.getContext("2d");
    ctx.beginPath(); ctx.moveTo(last.current.x, last.current.y); ctx.lineTo(p.x, p.y); ctx.stroke();
    last.current = p;
  };
  const end = () => { if (!drawing.current) return; drawing.current = false; onChange(canvasRef.current.toDataURL("image/png")); };
  const clear = () => {
    const c = canvasRef.current;
    c.getContext("2d").clearRect(0, 0, c.width, c.height);
    onChange(null);
  };

  return (
    <div>
      <canvas
        ref={canvasRef}
        className="sig-canvas"
        data-testid={testid}
        onMouseDown={start} onMouseMove={move} onMouseUp={end} onMouseLeave={end}
        onTouchStart={start} onTouchMove={move} onTouchEnd={end}
      />
      <div className="flex items-center justify-between mt-2 text-[13px]" style={{ color: "#5B6472" }}>
        <span>Sign above with finger or mouse.</span>
        <button className="underline min-h-[44px] px-2" onClick={clear} data-testid="sig-clear">Clear</button>
      </div>
    </div>
  );
}

const PAUSE_REASONS = [
  { value: "break", label: "Break" },
  { value: "waiting for parts", label: "Waiting for parts" },
  { value: "waiting for customer access", label: "Waiting for customer access" },
  { value: "other", label: "Other" },
];
const pauseLabel = value => PAUSE_REASONS.find(r => r.value === value)?.label || value;
const newRequestId = () => (window.crypto?.randomUUID
  ? window.crypto.randomUUID() : `${Date.now()}-${Math.random().toString(36).slice(2)}`);

function queuedPauseState(jobId, initial) {
  return (readStore(QUEUE_KEY) || []).reduce((state, item) => {
    if (item.jobId !== jobId || !item.kind) return state;
    return item.kind === "pause" ? { reason: item.payload.reason } : null;
  }, initial);
}

function ReportedProblem({ text, ticket }) {
  const [open, setOpen] = useState(false);
  const value = (text || "").trim();
  const long = value.length > 90 || value.includes("\n");
  const toggle = event => { event.stopPropagation(); event.preventDefault(); setOpen(o => !o); };
  const clamp = { display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical", overflow: "hidden" };
  return (
    <div className="text-[13.5px] mt-1" data-testid={`job-row-problem-${ticket}`}>
      <div className="font-medium">Reported problem</div>
      <div style={{ color: "#5B6472", lineHeight: 1.45, ...(open ? { whiteSpace: "pre-wrap" } : clamp) }}
           data-testid={`job-row-problem-text-${ticket}`}>
        {value || "No description given"}
      </div>
      {long && (
        <span role="button" tabIndex={0} className="underline text-[12.5px]" style={{ color: "#2563EB" }}
              onClick={toggle} onKeyDown={e => { if (e.key === "Enter" || e.key === " ") toggle(e); }}
              data-testid={`job-row-problem-toggle-${ticket}`}>
          {open ? "Show less" : "Show more"}
        </span>
      )}
    </div>
  );
}

function visitEntries(jobs) {
  const entries = [];
  const byVisit = {};
  jobs.forEach(job => {
    if (!job.visit_id) { entries.push({ jobs: [job] }); return; }
    if (!byVisit[job.visit_id]) { byVisit[job.visit_id] = { visitId: job.visit_id, jobs: [] }; entries.push(byVisit[job.visit_id]); }
    byVisit[job.visit_id].jobs.push(job);
  });
  entries.forEach(entry => entry.jobs.sort((a, b) => (a.visit_order ?? 0) - (b.visit_order ?? 0)));
  return entries;
}

function JobRow({ job, areas, sites, customers, onOpen, me }) {
  const site = sites.find(s => s.id === job.site_id);
  const area = areas.find(a => a.id === job.area_id);
  const customer = customers.find(c => c.id === job.customer_id);
  const st = job.priority === "equipment_down" ? "equipment_down" : job.status;
  const meta = STATUS_META[st] || STATUS_META.scheduled;
  const mine = job.assigned_tech_id === me;
  return (
    <button
      onClick={onOpen}
      className="w-full text-left bf-card flex items-center gap-3"
      style={{ minHeight: 44 }}
      data-testid={`job-row-${job.ticket_number}`}
    >
      <div className="flex-1">
        <div className="flex items-center gap-2">
          <div className="mono text-[12.5px] font-semibold">{job.ticket_number}</div>
          <span className="bf-chip" style={{ background: meta.chipBg, color: meta.chipText }}>
            <span className="bf-dot" style={{ background: meta.chipText }} />
            {meta.label}
          </span>
          {mine && <Badge className="h-5 text-[10px] px-2 rounded-full" style={{ background: "#EAF0FE", color: "#1E3A8A" }}>MINE</Badge>}
        </div>
        <div className="text-[15px] mt-2 font-semibold leading-snug" data-testid={`job-row-customer-${job.ticket_number}`}>{customer?.name}</div>
        <div className="text-[13.5px] mt-1" style={{ color: "#5B6472" }}>{site?.name} · {area?.name}</div>
        <div className="text-[13.5px] mt-1" style={{ color: "#5B6472" }}>
          {JOB_TYPE_LABELS[job.job_type] || "Repair"} · open {job.total_minutes ?? 0} min · active {job.active_minutes ?? 0} min
        </div>
        <ReportedProblem text={job.description} ticket={job.ticket_number} />
      </div>
      <ChevronRight size={20} color="#8A93A1" />
    </button>
  );
}

export default function Technician() {
  const { user: me } = useAuth();
  const isRealTech = me?.role === "technician";
  const [jobs, setJobs] = useState([]);
  const [techs, setTechs] = useState([]);
  const [areas, setAreas] = useState([]);
  const [sites, setSites] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [tech, setTech] = useState("");
  const [open, setOpen] = useState(null);
  const [queued, setQueued] = useState(() => (readStore(QUEUE_KEY) || []).length);
  const [, forceTick] = useState(0);

  const load = useCallback(async () => {
    const [j, t, a, s, c] = await Promise.all([
      client.get("/jobs"), client.get("/technicians"),
      client.get("/areas"), client.get("/sites"), client.get("/customers"),
    ]);
    setJobs(j.data); setTechs(t.data); setAreas(a.data); setSites(s.data); setCustomers(c.data);
    if (!isRealTech && t.data.length) setTech(current => current || t.data[0].id);
  }, [isRealTech]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { const i = setInterval(() => forceTick(x => x + 1), 30000); return () => clearInterval(i); }, []);

  const flushing = useRef(false);
  const flushQueue = useCallback(async () => {
    if (flushing.current) return;
    flushing.current = true;
    try {
    const pending = readStore(QUEUE_KEY) || [];
    if (!pending.length || !navigator.onLine) { setQueued(pending.length); return; }
    const remaining = [];
    let rejected = null;
    let offline = false;
    for (const item of pending) {
      if (offline) { remaining.push(item); continue; }
      try {
        if (item.path) await client.post(item.path, item.payload);
        else await client.patch(`/jobs/${item.jobId}`, item.payload);
      } catch (error) {
        if (error?.response) rejected = { item, error };
        else { remaining.push(item); offline = true; }
      }
    }
    writeStore(QUEUE_KEY, remaining);
    setQueued(remaining.length);
    if (pending.length !== remaining.length && !rejected) toast.success("Queued work sent");
    if (rejected) {
      toast.error(
        `${rejected.item.ticket || "A job"} was refused: ${rejected.error?.response?.data?.detail || "open it and check the close-out steps"}`,
        { duration: 12000 },
      );
    }
    if (pending.length !== remaining.length) load();
    } finally { flushing.current = false; }
  }, [load]);

  useEffect(() => {
    window.addEventListener("online", flushQueue);
    flushQueue();
    return () => window.removeEventListener("online", flushQueue);
  }, [flushQueue]);

  const mine = (isRealTech
    ? jobs.filter(j => !["complete", "tech_complete"].includes(j.status))
    : jobs.filter(j => j.assigned_tech_id === tech && !["complete", "tech_complete"].includes(j.status))
  ).sort((a, b) => new Date(a.scheduled_at || a.created_at) - new Date(b.scheduled_at || b.created_at));

  const scopedSites = isRealTech && me?.site_ids?.length ? sites.filter(s => me.site_ids.includes(s.id)) : null;

  return (
    <div data-testid="tech-page">
      <Toaster richColors position="top-right" />
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <div className="mono text-[11px] uppercase tracking-widest" style={{ color: "#5B6472" }}>Field view</div>
          <h1 className="text-[28px] font-bold" style={{ color: "#12161B", letterSpacing: "-0.022em", marginTop: "6px" }}>Today's Jobs</h1>
          {isRealTech && scopedSites && scopedSites.length > 0 && (
            <div className="text-[13px] mt-1" style={{ color: "#5B6472" }} data-testid="tech-scope-note">
              Scoped to: {scopedSites.map(s => s.name).join(" · ")}
            </div>
          )}
          {isRealTech && (!me?.site_ids || me.site_ids.length === 0) && (
            <div className="text-[13px] mt-1" style={{ color: "#5B6472" }}>All sites · no restriction</div>
          )}
        </div>
        {!isRealTech && (
          <div>
            <label className="font-condensed uppercase tracking-wider text-[12px] font-semibold mr-2" style={{ color: "#5B6472" }}>Signed in as</label>
            <select className="h-11 rounded-[10px] px-3 text-[13.5px] bg-white" style={{ border: "1px solid #E7EAEE", minHeight: 44 }}
              value={tech} onChange={e => setTech(e.target.value)} data-testid="tech-switch">
              {techs.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
            </select>
          </div>
        )}
      </div>

      {queued > 0 && (
        <div className="mt-5 rounded-[14px] px-4 py-3 flex items-center gap-3" data-testid="queued-banner"
             style={{ background: "#FFF6E5", color: "#96650A" }}>
          <CloudOff size={18} />
          <div className="text-[13.5px]" style={{ lineHeight: 1.5 }}>
            {queued} job{queued === 1 ? "" : "s"} waiting to send. It will go as soon as you have signal.
          </div>
          <button className="ml-auto underline text-[13px]" style={{ minHeight: 44 }} onClick={flushQueue} data-testid="queue-retry-btn">Try now</button>
        </div>
      )}

      {!storageWorks && (
        <div className="mt-5 rounded-[14px] px-4 py-3 text-[13.5px]" data-testid="storage-warning"
             style={{ background: "#FDECEC", color: "#B42318", lineHeight: 1.5 }}>
          This browser is blocking local storage, so a half-finished job is only held in memory.
          Finish and close each job before leaving the screen.
        </div>
      )}

      <div className="mt-7 max-w-[640px] space-y-3">
        {mine.length === 0 && <div className="text-[13.5px]" style={{ color: "#5B6472" }}>No jobs assigned. Nice work.</div>}
        {visitEntries(mine).map(entry => entry.visitId ? (
          <div key={entry.visitId} className="space-y-2" data-testid={`visit-group-${entry.visitId}`}>
            <div className="font-condensed uppercase tracking-wider text-[12px] font-semibold pt-1" style={{ color: "#1E3A8A" }}>
              Visit — {customers.find(c => c.id === entry.jobs[0].customer_id)?.name},{" "}
              {sites.find(s => s.id === entry.jobs[0].site_id)?.name},{" "}
              {entry.jobs[0].scheduled_at ? new Date(entry.jobs[0].scheduled_at).toLocaleDateString("en-GB", { timeZone: "America/Barbados" }) : "unscheduled"}
              {" "}— {(() => { const n = entry.jobs.reduce((sum, j) => sum + (j.visit_ticket ? (j.unit_count || 0) : 1), 0); return `${n} ${n === 1 ? "unit" : "units"}`; })()}
            </div>
            {entry.jobs.map(j => (
              <JobRow key={j.id} job={j} areas={areas} sites={sites} customers={customers}
                      onOpen={() => setOpen(j)} me={isRealTech ? me?.user_id : tech} />
            ))}
          </div>
        ) : (
          <JobRow key={entry.jobs[0].id} job={entry.jobs[0]} areas={areas} sites={sites} customers={customers}
                  onOpen={() => setOpen(entry.jobs[0])} me={isRealTech ? me?.user_id : tech} />
        ))}
      </div>

      {open && (
        <JobSheet
          job={open}
          onClose={() => setOpen(null)}
          onSaved={async () => { await load(); setOpen(null); }}
          onQueued={() => { setQueued((readStore(QUEUE_KEY) || []).length); setOpen(null); }}
          onQueueChange={() => { setQueued((readStore(QUEUE_KEY) || []).length); flushQueue(); }}
          techName={isRealTech ? (me?.name || me?.email) : (techs.find(t => t.id === tech)?.name)}
        />
      )}
    </div>
  );
}

const LABEL = "font-condensed uppercase tracking-wider text-[12px] font-semibold";

function Section({ title, right, children, testid }) {
  return (
    <section data-testid={testid}>
      <div className="flex items-center justify-between mb-2">
        <div className={LABEL} style={{ color: "#5B6472" }}>{title}</div>
        {right}
      </div>
      {children}
    </section>
  );
}

function UnitFields({ form, setForm, types, prefix }) {
  const spec = types.find(item => item.name === (form.type || "").trim().toLowerCase());
  const extras = spec?.custom_fields || [];
  const valueFor = label => (form.custom_fields || []).find(field => field.label === label)?.value || "";
  const setExtra = (label, value) => setForm(current => ({
    ...current,
    custom_fields: [...(current.custom_fields || []).filter(f => f.label !== label), { label, value }],
  }));
  const slug = label => label.toLowerCase().replace(/\s+/g, "-");
  return (
    <div className="space-y-2.5">
      <select className="w-full rounded-[10px] px-3 text-[13.5px] bg-white"
              style={{ border: "1px solid #E7EAEE", minHeight: 44 }}
              value={form.type} onChange={e => setForm(c => ({ ...c, type: e.target.value }))}
              data-testid={`${prefix}-type-select`}>
        <option value="">Choose equipment type</option>
        {types.map(t => <option key={t.name} value={t.name}>{t.name}</option>)}
      </select>
      <Input className="rounded-[10px] text-[13.5px]" style={{ minHeight: 44 }} placeholder="Make"
             value={form.make} onChange={e => setForm(c => ({ ...c, make: e.target.value }))}
             data-testid={`${prefix}-make-input`} />
      <Input className="rounded-[10px] text-[13.5px]" style={{ minHeight: 44 }} placeholder="Model"
             value={form.model} onChange={e => setForm(c => ({ ...c, model: e.target.value }))}
             data-testid={`${prefix}-model-input`} />
      <Input className="rounded-[10px] text-[13.5px] mono" style={{ minHeight: 44 }} placeholder="Serial number"
             value={form.serial_number} onChange={e => setForm(c => ({ ...c, serial_number: e.target.value }))}
             data-testid={`${prefix}-serial-input`} />
      {extras.map(field => (
        <div key={field.label}>
          <div className="text-[12.5px] mb-1" style={{ color: "#5B6472" }}>{field.label}</div>
          {(field.options || []).length > 0 ? (
            <select className="w-full rounded-[10px] px-3 text-[13.5px] bg-white"
                    style={{ border: "1px solid #E7EAEE", minHeight: 44 }}
                    value={valueFor(field.label)} onChange={e => setExtra(field.label, e.target.value)}
                    data-testid={`${prefix}-extra-${slug(field.label)}`}>
              <option value="">Choose {field.label.toLowerCase()}</option>
              {field.options.map(option => <option key={option} value={option}>{option}</option>)}
            </select>
          ) : (
            <Input className="rounded-[10px] text-[13.5px]" style={{ minHeight: 44 }}
                   value={valueFor(field.label)} onChange={e => setExtra(field.label, e.target.value)}
                   data-testid={`${prefix}-extra-${slug(field.label)}`} />
          )}
        </div>
      ))}
    </div>
  );
}

const EMPTY_UNIT = { type: "", make: "", model: "", serial_number: "", custom_fields: [] };

function PauseDialog({ open, reason, setReason, onCancel, onConfirm }) {
  return (
    <Dialog open={open} onOpenChange={value => { if (!value) onCancel(); }}>
      <DialogContent className="max-w-[420px] z-50" data-testid="pause-reason-dialog">
        <DialogHeader>
          <DialogTitle>Pause this job</DialogTitle>
          <DialogDescription>Your working time stops until you resume.</DialogDescription>
        </DialogHeader>
        <div className="space-y-2">
          {PAUSE_REASONS.map(option => (
            <label key={option.value} className="flex items-center gap-3 rounded-[12px] px-3 cursor-pointer"
                   style={{ background: reason === option.value ? "#EAF0FE" : "#F1F3F6", minHeight: 44 }}
                   data-testid={`pause-reason-option-${option.value.replace(/ /g, "-")}`}>
              <input type="radio" name="pause-reason" value={option.value} checked={reason === option.value}
                     onChange={() => setReason(option.value)} className="h-4 w-4 accent-[#2563EB]" />
              <span className="text-[13.5px]">{option.label}</span>
            </label>
          ))}
        </div>
        <DialogFooter className="mt-3">
          <Button variant="outline" className="bf-btn-ghost" onClick={onCancel} data-testid="pause-cancel-btn">Cancel</Button>
          <Button className="bf-btn-primary px-4" onClick={onConfirm} data-testid="pause-confirm-btn">Pause</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function JobSheet({ job, onClose, onSaved, onQueued, onQueueChange, techName }) {
  const draftKey = `blueflames-job-draft-${job.id}`;
  const [sheet, setSheet] = useState(null);
  const [items, setItems] = useState({});
  const [readings, setReadings] = useState({});
  const [parts, setParts] = useState([]);
  const [photos, setPhotos] = useState([]);
  const [findings, setFindings] = useState("");
  const [eqStatus, setEqStatus] = useState("running");
  const [sig, setSig] = useState(null);
  const [saving, setSaving] = useState(false);
  const [savedMark, setSavedMark] = useState(false);
  const [partQuery, setPartQuery] = useState("");
  const [arrived, setArrived] = useState(Boolean(job.on_site_at));
  const [identifyForm, setIdentifyForm] = useState(EMPTY_UNIT);
  const [addOpen, setAddOpen] = useState(false);
  const [addPick, setAddPick] = useState("");
  const [addForm, setAddForm] = useState(EMPTY_UNIT);
  const [busy, setBusy] = useState(false);
  const [paused, setPaused] = useState(null);
  const [pauseOpen, setPauseOpen] = useState(false);
  const [pauseReason, setPauseReason] = useState("break");
  const [timerBase, setTimerBase] = useState({ minutes: 0, at: Date.now() });
  const [, setTimerTick] = useState(0);
  useEffect(() => { const i = setInterval(() => setTimerTick(t => t + 1), 30000); return () => clearInterval(i); }, []);
  const timerRunning = arrived && !paused && !sheet?.locked;
  const liveMinutes = timerBase.minutes + (timerRunning ? Math.floor((Date.now() - timerBase.at) / 60000) : 0);
  const freezeTimer = () => setTimerBase({ minutes: liveMinutes, at: Date.now() });

  const sendPause = async kind => {
    const stamp = new Date().toISOString();
    const payload = kind === "pause"
      ? { reason: pauseReason, paused_at: stamp, request_id: newRequestId() }
      : { resumed_at: stamp, request_id: newRequestId() };
    const path = `/jobs/${job.id}/${kind === "pause" ? "pause" : "tech-resume"}`;
    const next = kind === "pause" ? { reason: pauseReason } : null;
    setPauseOpen(false);
    const enqueue = () => {
      writeStore(QUEUE_KEY, [...(readStore(QUEUE_KEY) || []),
        { jobId: job.id, ticket: job.ticket_number, kind, path, payload, queuedAt: stamp }]);
      freezeTimer();
      setPaused(next);
      onQueueChange();
    };
    if ((readStore(QUEUE_KEY) || []).some(item => item.jobId === job.id)) { enqueue(); return; }
    try {
      await client.post(path, payload);
      freezeTimer();
      setPaused(next);
      toast.success(kind === "pause" ? "Job paused" : "Job resumed");
    } catch (error) {
      if (!error?.response) { enqueue(); toast.info("No signal — this will send when you are back online"); return; }
      toast.error(error.response.data?.detail || "Could not update the pause");
    }
  };

  const reloadSheet = async () => {
    const response = await client.get(`/jobs/${job.id}/sheet`);
    setSheet(response.data);
  };

  const saveIdentified = async () => {
    try {
      setBusy(true);
      await client.post(`/jobs/${job.id}/identify-equipment`, identifyForm);
      setIdentifyForm(EMPTY_UNIT);
      await reloadSheet();
      toast.success("Equipment linked to this job");
    } catch (error) {
      toast.error(error?.response?.data?.detail || "Could not save this unit");
    } finally { setBusy(false); }
  };

  const addAnotherItem = async () => {
    try {
      setBusy(true);
      const payload = addPick ? { equipment_id: addPick } : { new_equipment: addForm };
      const response = await client.post(`/jobs/${job.id}/add-item`, payload);
      setAddOpen(false); setAddPick(""); setAddForm(EMPTY_UNIT);
      toast.success(`${response.data.ticket_number} raised for the extra item`);
      onSaved();
    } catch (error) {
      toast.error(error?.response?.data?.detail || "Could not add the item");
    } finally { setBusy(false); }
  };

  useEffect(() => {
    let cancelled = false;
    client.get(`/jobs/${job.id}/sheet`).then(r => {
      if (cancelled) return;
      setSheet(r.data);
      const draft = readStore(draftKey);
      const serverItems = {};
      r.data.groups.forEach(g => g.items.forEach(i => {
        serverItems[i.id] = { state: i.state, reason: i.reason, note: i.note, photo_file_id: i.photo_file_id };
      }));
      setItems(draft?.items || serverItems);
      setReadings(draft?.readings
        || Object.fromEntries((r.data.reading_values || []).map(v => [v.id, v.value])));
      setParts(draft?.parts || job.parts_used || []);
      setPhotos(draft?.photos || job.photos || []);
      setFindings(draft?.findings ?? (job.work_performed || ""));
      setEqStatus(draft?.eqStatus || job.equipment_status_on_leaving || "running");
      setSig(draft?.signature || job.signature || null);
      setArrived(Boolean(r.data.header.on_site_at));
      setPaused(queuedPauseState(job.id, r.data.header.tech_paused ? { reason: r.data.header.tech_pause_reason } : null));
      setTimerBase({ minutes: r.data.header.working_minutes || 0, at: Date.now() });
    }).catch(() => setSheet(null));
    return () => { cancelled = true; };
  }, [job.id, draftKey]);

  useEffect(() => {
    if (!sheet) return;
    writeStore(draftKey, { items, readings, parts, photos, findings, eqStatus, signature: sig });
    setSavedMark(true);
  }, [sheet, draftKey, items, readings, parts, photos, findings, eqStatus, sig]);

  const specs = sheet?.readings || [];
  const readingFailures = useMemo(() => specs.flatMap(spec => {
    const raw = readings[spec.id];
    if (raw === "" || raw === undefined || raw === null) return [];
    const value = Number(raw);
    if (Number.isNaN(value)) return [];
    const low = spec.min, high = spec.max;
    if ((low !== null && low !== undefined && value < low) || (high !== null && high !== undefined && value > high)) {
      return [{ id: spec.id, text: `${spec.label} ${value}${spec.unit || ""} outside ${low} to ${high}${spec.unit || ""}` }];
    }
    return [];
  }), [specs, readings]);

  const groups = useMemo(() => {
    if (!sheet) return [];
    const extra = readingFailures.length > 0 ? (sheet.conditional_groups || []) : [];
    return [...sheet.groups, ...extra];
  }, [sheet, readingFailures]);

  const flatItems = useMemo(() => groups.flatMap(g => g.items.map(i => ({
    ...i, ...(items[i.id] || {}), group_id: g.id,
  }))), [groups, items]);

  const answeredGroups = groups.filter(g => g.items.every(i => (items[i.id]?.state || "unanswered") !== "unanswered")).length;
  const failures = flatItems.filter(i => i.state === "fail");
  const unevidenced = failures.filter(i => !i.note && !i.photo_file_id);
  const naMissingReason = flatItems.filter(i => i.state === "na" && !i.reason);
  const checklistComplete = groups.length > 0
    && flatItems.every(i => (i.state || "unanswered") !== "unanswered")
    && unevidenced.length === 0 && naMissingReason.length === 0;

  const setItem = (id, changes) => setItems(current => ({ ...current, [id]: { ...(current[id] || {}), ...changes } }));
  const allOk = (group) => setItems(current => {
    const next = { ...current };
    group.items.forEach(i => { next[i.id] = { state: "pass", reason: "", note: "", photo_file_id: "" }; });
    return next;
  });

  const addPart = (name) => {
    if (!name) return;
    setParts(current => {
      const index = current.findIndex(p => p.name === name);
      if (index >= 0) return current.map((p, i) => i === index ? { ...p, qty: (Number(p.qty) || 1) + 1 } : p);
      return [...current, { name, qty: 1 }];
    });
    setPartQuery("");
  };
  const stepPart = (index, delta) => setParts(current => current
    .map((p, i) => i === index ? { ...p, qty: Math.max(1, (Number(p.qty) || 1) + delta) } : p));
  const rmPart = (index) => setParts(current => current.filter((_, i) => i !== index));

  const uploadBlob = async (blob, filename, kind = "photo") => {
    const data = new FormData();
    data.append("file", blob, filename);
    data.append("kind", kind);
    const response = await client.post(`/jobs/${job.id}/files`, data);
    return { file_id: response.data.file_id, kind: response.data.kind };
  };

  const compressImage = file => new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = reject;
    reader.onload = () => {
      const image = new Image();
      image.onerror = reject;
      image.onload = () => {
        const scale = Math.min(1, 1800 / Math.max(image.width, image.height));
        const canvas = document.createElement("canvas");
        canvas.width = Math.round(image.width * scale);
        canvas.height = Math.round(image.height * scale);
        canvas.getContext("2d").drawImage(image, 0, 0, canvas.width, canvas.height);
        canvas.toBlob(blob => blob ? resolve(blob) : reject(new Error("Compression failed")), "image/jpeg", 0.82);
      };
      image.src = reader.result;
    };
    reader.readAsDataURL(file);
  });

  const onFile = async (event, forItemId = "") => {
    const files = [...event.target.files].slice(0, 6);
    event.target.value = "";
    try {
      const uploaded = await Promise.all(files.map(async file => {
        const blob = file.size > 2 * 1024 * 1024 ? await compressImage(file) : file;
        return uploadBlob(blob, `${file.name.replace(/\.[^.]+$/, "")}.jpg`);
      }));
      const tagged = uploaded.map(u => ({ ...u, caption: forItemId ? "Fault found" : "" }));
      setPhotos(current => [...current, ...tagged]);
      if (forItemId && tagged[0]) setItem(forItemId, { photo_file_id: tagged[0].file_id });
      toast.success(`${tagged.length} photo${tagged.length === 1 ? "" : "s"} stored`);
    } catch (error) {
      toast.error(error?.response?.data?.detail || "Photo upload failed — your work is still saved on this device");
    }
  };

  const photoSrc = photo => typeof photo === "string" ? photo : `${API}/files/${photo.file_id}`;

  const gateMessage = () => {
    if (!arrived) return "Tap Start job when you arrive";
    if (sheet?.checklist_required && !checklistComplete) {
      if (unevidenced.length) return "Each failed item needs a note or a photo";
      if (naMissingReason.length) return "Pick a reason for every not applicable";
      return `Checklist: ${answeredGroups} of ${groups.length} groups done`;
    }
    if (!eqStatus) return "Record equipment status on leaving";
    if (!sig) return "Customer signature required";
    return "";
  };
  const blocked = gateMessage();

  const buildPayload = (newStatus) => {
    const payload = {
      work_performed: findings,
      parts_used: parts.map(p => ({ name: p.name, qty: Number(p.qty) || 1 })),
      equipment_status_on_leaving: eqStatus,
      readings: Object.entries(readings).map(([id, value]) => ({ id, value })),
      photos,
      signature: sig,
    };
    if (newStatus) payload.status = newStatus;
    return payload;
  };

  const save = async (newStatus) => {
    let payload;
    let signatureBlob = null;
    if (typeof sig === "string" && sig.startsWith("data:image/")) {
      signatureBlob = dataUrlToBlob(sig);
      if (!signatureBlob) { toast.error("Please sign again"); return; }
    }
    try {
      setSaving(true);
      if (groups.length) {
        await client.post(`/jobs/${job.id}/checklist`, {
          template_name: "Field service close-out",
          template_version: 2,
          items: flatItems.map(i => ({
            id: i.id, label: i.label, group_id: i.group_id,
            state: i.state || "unanswered", reason: i.reason || "",
            note: i.note || "", photo_file_id: i.photo_file_id || "",
          })),
          complete: newStatus === "tech_complete" && sheet?.checklist_required,
        });
      }
      payload = buildPayload(newStatus);
      if (signatureBlob) {
        payload.signature = await uploadBlob(signatureBlob, "customer-signature.png", "signature");
        setSig(payload.signature);
      }
      await client.patch(`/jobs/${job.id}`, payload);
      if (newStatus === "on_site") {
        setTimerBase({ minutes: 0, at: Date.now() });
        setArrived(true);
        toast.success("Arrival recorded");
        setSaving(false);
        return;
      }
      clearStore(draftKey);
      toast.success(newStatus === "tech_complete" ? "Sent to the office for review" : "Saved");
      onSaved();
    } catch (e) {
      if (payload && (!navigator.onLine || !e?.response)) {
        const pending = readStore(QUEUE_KEY) || [];
        writeStore(QUEUE_KEY, [...pending, {
          jobId: job.id, ticket: job.ticket_number, payload, queuedAt: new Date().toISOString(),
        }]);
        toast.info("No signal — saved and waiting to send");
        onQueued();
        return;
      }
      const detail = e?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Save refused. Check the steps listed on the Close button.");
    } finally { setSaving(false); }
  };

  const header = sheet?.header;
  const suggestions = (sheet?.parts_catalogue || []);
  const ordered = [...(sheet?.recent_parts || []).filter(n => suggestions.includes(n)),
                   ...suggestions.filter(n => !(sheet?.recent_parts || []).includes(n))];
  const filtered = partQuery
    ? ordered.filter(n => n.toLowerCase().includes(partQuery.toLowerCase()))
    : ordered.slice(0, 6);

  return (
    <div className="fixed inset-0 z-40 flex" data-testid="job-drawer">
      <div className="flex-1 bg-black/40 hidden md:block" onClick={onClose} />
      <div className="w-full md:max-w-[560px] bg-white h-full overflow-y-auto border-l" style={{ borderColor: "#EFF1F4" }}>
        <div className="sticky top-0 z-10 bg-white border-b px-4 py-3" style={{ borderColor: "#EFF1F4" }} data-testid="sheet-header">
          <div className="flex items-start justify-between gap-2">
            <div className="min-w-0">
              <div className="flex items-center gap-2">
                <span className="mono text-[12.5px] font-semibold">{job.ticket_number}</span>
                <span className="bf-chip" style={{ background: "#EAF0FE", color: "#1E3A8A" }}>
                  {JOB_TYPE_LABELS[header?.job_type] || "Repair"}
                </span>
              </div>
              <div className="text-[15px] font-semibold mt-1 truncate">
                {header?.visit_ticket ? `Maintenance visit · ${header.unit_count || 0} units` : (
                  <>{header?.make} {header?.model} <span style={{ color: "#5B6472", fontWeight: 400 }}>{header?.type}</span></>
                )}
              </div>
              {!header?.visit_ticket && (
                <div className="mono text-[12px] mt-0.5" style={{ color: "#5B6472" }} data-testid="sheet-serial">
                  SN {header?.serial_number || "not recorded"}
                </div>
              )}
              <div className="text-[12.5px] mt-0.5 truncate" style={{ color: "#5B6472" }}>
                {header?.site} · {header?.area}
              </div>
            </div>
            <div className="flex flex-col items-end gap-1">
              <button onClick={onClose} data-testid="drawer-close"
                      className="rounded-[10px] hover:bg-[#F6F7F9] flex items-center justify-center"
                      style={{ minWidth: 44, minHeight: 44 }}><X size={20} /></button>
              <div className="mono text-[11px]" style={{ color: "#5B6472" }} data-testid="sheet-elapsed">
                {liveMinutes} min working
              </div>
            </div>
          </div>
          {groups.length > 0 && (
            <div className="mt-2 text-[12px] mono" style={{ color: "#5B6472" }} data-testid="sheet-progress">
              {answeredGroups} of {groups.length} groups
            </div>
          )}
        </div>

        {!sheet && <div className="p-5 text-[13.5px]" style={{ color: "#5B6472" }}>Loading job sheet…</div>}

        {sheet && (
          <div className="px-4 pt-4 pb-40 space-y-7">
            <div className="rounded-[14px] p-4" style={{ background: "#F1F3F6" }} data-testid="sheet-known">
              <div className="text-[13px]" style={{ color: "#5B6472" }}>{header.customer}</div>
              <div className="text-[13.5px] mt-0.5" style={{ lineHeight: 1.5 }}>{header.address}</div>
              {header.access_notes && (
                <div className="text-[13px] mt-1" style={{ color: "#5B6472", lineHeight: 1.5 }}>Access: {header.access_notes}</div>
              )}
              {(header.custom_fields || []).length > 0 && (
                <div className="mt-2 space-y-0.5" data-testid="sheet-custom-fields">
                  {header.custom_fields.map(field => (
                    <div key={field.label} className="text-[13px]" style={{ lineHeight: 1.5 }}>
                      {field.label}: <span style={{ color: "#5B6472" }}>{field.value || "not recorded"}</span>
                    </div>
                  ))}
                </div>
              )}
              <div className="text-[13px] mt-1" style={{ lineHeight: 1.5 }}>Reported: {header.reported_fault}</div>
              {sheet.added_on_site && (
                <div className="text-[12.5px] mt-1" style={{ color: "#0B6080" }} data-testid="sheet-added-on-site">
                  Added on site · from {sheet.parent_ticket_number}
                </div>
              )}
              {sheet.history?.length > 0 && (
                <div className="mt-2 text-[12.5px]" style={{ color: "#5B6472", lineHeight: 1.5 }} data-testid="sheet-history">
                  Last visits: {sheet.history.map(h => h.ticket_number).join(", ")}
                </div>
              )}
            </div>

            {sheet.needs_equipment && (
              <Section title="Identify equipment" testid="sheet-identify">
                <div className="text-[12.5px] mb-2" style={{ color: "#5B6472", lineHeight: 1.5 }}>
                  This job has no unit attached. Record what you found on site.
                </div>
                <UnitFields form={identifyForm} setForm={setIdentifyForm}
                            types={sheet.equipment_type_options || []} prefix="identify" />
                <Button className="mt-3 w-full bf-btn-primary" style={{ minHeight: 48 }}
                        disabled={busy || !identifyForm.type} onClick={saveIdentified}
                        data-testid="identify-save-btn">
                  Save equipment
                </Button>
              </Section>
            )}

            {arrived && !sheet.locked && (
              <Section title="Another item on this visit" testid="sheet-add-item">
                {!addOpen ? (
                  <Button variant="outline" className="bf-btn-ghost w-full" style={{ minHeight: 48 }}
                          onClick={() => setAddOpen(true)} data-testid="add-item-open-btn">
                    Add another item
                  </Button>
                ) : (
                  <div className="space-y-2.5">
                    <select className="w-full rounded-[10px] px-3 text-[13.5px] bg-white"
                            style={{ border: "1px solid #E7EAEE", minHeight: 44 }}
                            value={addPick} onChange={e => setAddPick(e.target.value)}
                            data-testid="add-item-equipment-select">
                      <option value="">New unit not on record</option>
                      {(sheet.area_equipment || []).map(unit => (
                        <option key={unit.id} value={unit.id}>
                          {[unit.type, unit.make, unit.model].filter(Boolean).join(" ")}
                          {unit.serial_number ? ` · ${unit.serial_number}` : ""}
                        </option>
                      ))}
                    </select>
                    {!addPick && (
                      <UnitFields form={addForm} setForm={setAddForm}
                                  types={sheet.equipment_type_options || []} prefix="add-item" />
                    )}
                    <div className="flex items-center gap-2">
                      <Button variant="outline" className="bf-btn-ghost px-4" style={{ minHeight: 48 }}
                              onClick={() => { setAddOpen(false); setAddPick(""); setAddForm(EMPTY_UNIT); }}
                              data-testid="add-item-cancel-btn">Cancel</Button>
                      <Button className="bf-btn-primary flex-1" style={{ minHeight: 48 }}
                              disabled={busy || (!addPick && !addForm.type)} onClick={addAnotherItem}
                              data-testid="add-item-save-btn">
                        Raise ticket for this item
                      </Button>
                    </div>
                  </div>
                )}
              </Section>
            )}

            {specs.length > 0 && (
              <Section title="Readings" testid="sheet-readings">
                <div className="space-y-2.5">
                  {specs.map(spec => {
                    const bad = readingFailures.some(f => f.id === spec.id);
                    return (
                      <div key={spec.id} className="grid grid-cols-[1fr_110px] gap-2 items-center">
                        <div>
                          <div className="text-[13.5px] font-medium">{spec.label}</div>
                          <div className="mono text-[11.5px]" style={{ color: bad ? "#B42318" : "#5B6472" }}>
                            {spec.min} to {spec.max} {spec.unit}
                          </div>
                        </div>
                        <Input
                          type="number" inputMode="decimal"
                          className="rounded-[10px] mono text-[13.5px] text-right"
                          style={{ minHeight: 44, borderColor: bad ? "#B42318" : undefined }}
                          value={readings[spec.id] ?? ""}
                          onChange={e => setReadings(c => ({ ...c, [spec.id]: e.target.value }))}
                          placeholder={spec.unit}
                          data-testid={`reading-${spec.id}`}
                        />
                      </div>
                    );
                  })}
                </div>
                {readingFailures.length > 0 && (
                  <div className="mt-3 rounded-[10px] px-3 py-2.5" style={{ background: "#FDECEC" }} data-testid="reading-failures">
                    {readingFailures.map(f => (
                      <div key={f.id} className="text-[13px] flex items-start gap-2" style={{ color: "#B42318", lineHeight: 1.5 }}>
                        <AlertTriangle size={14} className="mt-0.5 shrink-0" /> <span>{f.text}</span>
                      </div>
                    ))}
                  </div>
                )}
              </Section>
            )}

            <section data-testid="job-checklist">
              <div className="flex items-center justify-between mb-2">
                <div className={LABEL} style={{ color: "#5B6472" }}>
                  {sheet.checklist_required ? "Required checklist" : "Checklist"}
                </div>
                {!sheet.locked && (
                  <button className="text-[13px] px-3 underline" style={{ minHeight: 44, color: "#2563EB" }}
                          onClick={() => save()} data-testid="save-checklist-button">Save</button>
                )}
              </div>
              <div className="text-[12.5px] mb-3" style={{ color: "#5B6472", lineHeight: 1.5 }}>
                Everything is assumed in order. Report only what you found.
              </div>
              <div className="space-y-4">
                {groups.map(group => {
                  const done = group.items.every(i => (items[i.id]?.state || "unanswered") !== "unanswered");
                  return (
                    <div key={group.id} className="bf-card" style={{ padding: "14px" }} data-testid={`checklist-group-${group.id}`}>
                      <div className="flex items-center justify-between gap-2">
                        <div className="text-[14px] font-semibold">{group.label}</div>
                        <Button
                          type="button" variant={done ? "outline" : "default"}
                          className={`text-[12.5px] px-4 ${done ? "bf-btn-ghost" : "bf-btn-primary"}`}
                          style={{ minHeight: 44 }} disabled={sheet.locked}
                          onClick={() => allOk(group)} data-testid={`group-allok-${group.id}`}
                        >
                          <Check size={15} className="mr-1.5" /> All OK
                        </Button>
                      </div>
                      <div className="mt-2 divide-y" style={{ borderColor: "#EFF1F4" }}>
                        {group.items.map(item => {
                          const state = items[item.id]?.state || "unanswered";
                          const row = items[item.id] || {};
                          return (
                            <div key={item.id} className="pt-3 pb-1" data-testid={`checklist-item-${item.id}`}>
                              <div className="text-[13.5px] font-medium">{item.label}</div>
                              <div className="mt-2 flex items-center gap-2 flex-wrap">
                                <Button type="button" variant={state === "pass" ? "default" : "outline"}
                                        className={`text-[12.5px] px-4 ${state === "pass" ? "bf-btn-primary" : "bf-btn-ghost"}`}
                                        style={{ minHeight: 44 }} disabled={sheet.locked}
                                        onClick={() => setItem(item.id, { state: "pass", reason: "", note: "", photo_file_id: "" })}
                                        data-testid={`checklist-done-${item.id}`}>Pass</Button>
                                <Button type="button" variant="outline"
                                        className="text-[12.5px] px-4"
                                        style={{ minHeight: 44, ...(state === "fail" ? { background: "#FDECEC", color: "#B42318", borderColor: "#B42318" } : {}) }}
                                        disabled={sheet.locked}
                                        onClick={() => setItem(item.id, { state: "fail", reason: "" })}
                                        data-testid={`checklist-fail-${item.id}`}>Fail</Button>
                                <Button type="button" variant="outline"
                                        className="text-[12.5px] px-4"
                                        style={{ minHeight: 44, ...(state === "na" ? { background: "#EAF0FE", color: "#1E3A8A", borderColor: "#1E3A8A" } : {}) }}
                                        disabled={sheet.locked}
                                        onClick={() => setItem(item.id, { state: "na", note: "", photo_file_id: "" })}
                                        data-testid={`checklist-na-${item.id}`}>N/A</Button>
                              </div>
                              {state === "na" && (
                                <select
                                  className="mt-2 w-full rounded-[10px] px-3 text-[13.5px] bg-white"
                                  style={{ border: "1px solid #E7EAEE", minHeight: 44 }}
                                  value={row.reason || ""} disabled={sheet.locked}
                                  onChange={e => setItem(item.id, { reason: e.target.value })}
                                  data-testid={`checklist-reason-${item.id}`}
                                >
                                  <option value="">Why not applicable?</option>
                                  {sheet.na_reasons.map(reason => <option key={reason} value={reason}>{reason}</option>)}
                                </select>
                              )}
                              {state === "fail" && (
                                <div className="mt-2 space-y-2">
                                  <div className="text-[12.5px]" style={{ color: "#B42318" }}>
                                    Add a note or a photo — one is enough.
                                  </div>
                                  <div className="flex items-center gap-2">
                                    <Input className="rounded-[10px] text-[13.5px]" style={{ minHeight: 44 }}
                                           value={row.note || ""} disabled={sheet.locked}
                                           onChange={e => setItem(item.id, { note: e.target.value })}
                                           placeholder="What did you find?"
                                           data-testid={`checklist-note-${item.id}`} />
                                    <label className="rounded-[10px] border flex items-center justify-center cursor-pointer shrink-0"
                                           style={{ minWidth: 44, minHeight: 44, borderColor: row.photo_file_id ? "#136B36" : "#E7EAEE" }}
                                           data-testid={`checklist-photo-${item.id}`}>
                                      <Camera size={17} color={row.photo_file_id ? "#136B36" : "#5B6472"} />
                                      <input type="file" accept="image/*" className="hidden"
                                             onChange={e => onFile(e, item.id)} />
                                    </label>
                                  </div>
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  );
                })}
              </div>
              {failures.length > 0 && (
                <div className="mt-3 rounded-[10px] px-3 py-2.5" style={{ background: "#FDECEC" }} data-testid="failure-summary">
                  <div className="text-[12.5px] font-semibold" style={{ color: "#B42318" }}>
                    {failures.length} exception{failures.length === 1 ? "" : "s"} will lead the customer report
                  </div>
                </div>
              )}
            </section>

            <Section title="Parts used" testid="sheet-parts">
              <Input className="rounded-[10px] text-[13.5px]" style={{ minHeight: 44 }}
                     value={partQuery} onChange={e => setPartQuery(e.target.value)}
                     placeholder="Search parts for this unit" data-testid="part-search" />
              <div className="mt-2 flex flex-wrap gap-2">
                {filtered.map(name => (
                  <button key={name} onClick={() => addPart(name)}
                          className="bf-chip text-[13px] px-3"
                          style={{ background: "#EAF0FE", color: "#1E3A8A", minHeight: 44 }}
                          data-testid={`part-pick-${name.toLowerCase().replace(/\s+/g, "-")}`}>
                    + {name}
                  </button>
                ))}
                <button onClick={() => addPart(partQuery.trim() || "Other part")}
                        className="bf-chip text-[13px] px-3" style={{ background: "#F1F3F6", color: "#12161B", minHeight: 44 }}
                        data-testid="add-part-btn">
                  + {partQuery.trim() ? `Add "${partQuery.trim()}"` : "Other part"}
                </button>
              </div>
              <div className="mt-3 space-y-2.5">
                {parts.map((p, i) => (
                  <div key={`${p.name}-${i}`} className="flex items-center gap-2">
                    <div className="flex-1 text-[13.5px]" data-testid={`part-name-${i}`}>{p.name}</div>
                    <button onClick={() => stepPart(i, -1)} className="rounded-[10px] border flex items-center justify-center"
                            style={{ minWidth: 44, minHeight: 44, borderColor: "#E7EAEE" }} data-testid={`part-minus-${i}`}>
                      <Minus size={15} />
                    </button>
                    <div className="mono text-[14px] w-8 text-center" data-testid={`part-qty-${i}`}>{p.qty}</div>
                    <button onClick={() => stepPart(i, 1)} className="rounded-[10px] border flex items-center justify-center"
                            style={{ minWidth: 44, minHeight: 44, borderColor: "#E7EAEE" }} data-testid={`part-plus-${i}`}>
                      <Plus size={15} />
                    </button>
                    <button onClick={() => rmPart(i)} className="rounded-[10px] flex items-center justify-center hover:bg-[#F6F7F9]"
                            style={{ minWidth: 44, minHeight: 44 }} data-testid={`part-del-${i}`}>
                      <Trash2 size={16} color="#B42318" />
                    </button>
                  </div>
                ))}
                {parts.length === 0 && <div className="text-[13px]" style={{ color: "#8A93A1" }}>No parts used.</div>}
              </div>
            </Section>

            <Section title="Photos" testid="sheet-photos">
              <div className="flex items-center gap-2 flex-wrap">
                {photos.map((p, i) => (
                  <div key={photoSrc(p)} className="relative">
                    <img src={photoSrc(p)} alt="" className="h-16 w-16 object-cover rounded-[10px]" data-testid={`photo-thumb-${i}`} />
                    <button onClick={() => setPhotos(ps => ps.filter((_, j) => j !== i))} data-testid={`photo-remove-${i}`}
                            className="absolute -top-2 -right-2 rounded-full bg-white flex items-center justify-center"
                            style={{ minWidth: 28, minHeight: 28, boxShadow: "0 1px 2px rgba(16,24,32,.12), 0 4px 10px rgba(16,24,32,.12)" }}>
                      <X size={13} />
                    </button>
                    <select className="mt-1 w-16 rounded-[8px] text-[10.5px] bg-white" style={{ border: "1px solid #E7EAEE" }}
                            value={p.caption || ""} data-testid={`photo-caption-${i}`}
                            onChange={e => setPhotos(ps => ps.map((x, j) => j === i ? { ...x, caption: e.target.value } : x))}>
                      <option value="">Label</option>
                      {sheet.photo_captions.map(caption => <option key={caption} value={caption}>{caption}</option>)}
                    </select>
                  </div>
                ))}
                <label className="h-16 w-16 rounded-[10px] border-2 border-dashed flex items-center justify-center cursor-pointer"
                       style={{ borderColor: "#E7EAEE", color: "#5B6472", minWidth: 44, minHeight: 44 }} data-testid="photo-upload">
                  <Camera size={18} />
                  <input type="file" accept="image/*" multiple className="hidden" onChange={e => onFile(e)} />
                </label>
              </div>
            </Section>

            <Section title="Findings" testid="sheet-findings">
              <Textarea className="rounded-[10px] text-[13.5px]" rows={3} value={findings}
                        onChange={e => setFindings(e.target.value)} data-testid="work-input"
                        placeholder="Anything the customer should know, in your words" />
              {(sheet.quick_phrases || []).length > 0 && (
                <div className="mt-2 flex flex-wrap gap-2">
                  {sheet.quick_phrases.map(phrase => (
                    <button key={phrase} onClick={() => setFindings(t => (t ? `${t} ${phrase}` : phrase))}
                            className="bf-chip text-[12.5px] px-3" style={{ background: "#F1F3F6", minHeight: 44 }}>
                      {phrase}
                    </button>
                  ))}
                </div>
              )}
              <div className="mt-2 text-[12.5px]" style={{ color: "#5B6472", lineHeight: 1.5 }}>
                The customer report is built from your exceptions, readings, parts and this text. You write once.
              </div>
            </Section>

            <Section title="Equipment on leaving" testid="sheet-leaving">
              <div className="mb-3" data-testid="working-time-readonly">
                <div className="text-[13.5px] font-medium">Working time</div>
                <div className="mono text-[13.5px] mt-1" data-testid="working-time-value">
                  {Math.floor(liveMinutes / 60)} h {liveMinutes % 60} min{paused ? " (paused)" : ""}
                </div>
                <div className="mt-1 text-[12.5px] bf-help">
                  Counted from Start job, without pauses. The office sets the labour hours.
                </div>
              </div>
              <select className="w-full rounded-[10px] px-3 text-[13.5px] bg-white"
                      style={{ border: "1px solid #E7EAEE", minHeight: 44 }}
                      value={eqStatus} onChange={e => setEqStatus(e.target.value)} data-testid="eq-status-select">
                <option value="running">Running</option>
                <option value="running-monitor">Running — monitor</option>
                <option value="out_of_service">Out of service</option>
              </select>
            </Section>

            <Section title="Customer signature" testid="sheet-signature">
              <SignaturePad value={sig} onChange={setSig} testid="signature-pad" />
            </Section>
          </div>
        )}

        {sheet && (
          <div className="fixed bottom-0 left-0 right-0 md:left-auto md:w-[560px] bg-white border-t px-4 pt-3 pb-4"
               style={{ borderColor: "#EFF1F4", boxShadow: "0 -6px 18px rgba(16,24,32,.06)" }}>
            <div className="flex items-center justify-between text-[12px] mb-2">
              <span style={{ color: savedMark ? "#136B36" : "#5B6472" }} data-testid="draft-saved-note">
                {savedMark ? (storageWorks ? "Saved on this device" : "Held in memory — storage blocked") : "Not saved yet"}
              </span>
              {paused && <span style={{ color: "#96650A" }} data-testid="sheet-paused-note">Paused: {pauseLabel(paused.reason)}</span>}
              {blocked && <span style={{ color: "#B42318" }} data-testid="close-blocked-reason">{blocked}</span>}
            </div>
            <div className="flex items-center gap-2">
              {!arrived && (
                <Button variant="outline" className="bf-btn-ghost px-4 flex-1" style={{ minHeight: 48 }}
                        onClick={() => save("on_site")} disabled={saving} data-testid="start-btn">
                  Start job (on site)
                </Button>
              )}
              <Button variant="outline" className="bf-btn-ghost px-4" style={{ minHeight: 48 }}
                      onClick={() => save()} disabled={saving} data-testid="save-btn">Save</Button>
              {arrived && !sheet.locked && header?.status !== "on_hold" && (paused ? (
                <Button variant="outline" className="bf-btn-ghost px-4" style={{ minHeight: 48 }}
                        onClick={() => sendPause("resume")} disabled={saving} data-testid="tech-resume-btn">Resume</Button>
              ) : (
                <Button variant="outline" className="bf-btn-ghost px-4" style={{ minHeight: 48 }}
                        onClick={() => setPauseOpen(true)} disabled={saving} data-testid="tech-pause-btn">Pause</Button>
              ))}
              <Button className="text-white px-4 flex-1 bf-btn-primary"
                      style={{ minHeight: 48, background: blocked ? "#94A3B8" : "#2563EB", boxShadow: blocked ? "none" : undefined }}
                      disabled={!!blocked || saving} onClick={() => save("tech_complete")} data-testid="close-job-btn">
                {blocked ? "Tech complete" : `Tech complete · ${techName || "signed"}`}
              </Button>
            </div>
          </div>
        )}
      </div>
      <PauseDialog open={pauseOpen} reason={pauseReason} setReason={setPauseReason}
                   onCancel={() => setPauseOpen(false)} onConfirm={() => sendPause("pause")} />
    </div>
  );
}
